import React from "react";
import PropTypes from "prop-types";
import { Button, Empty, Icon, Popconfirm } from "antd";
import PolicyRowEditor from "./policy-editor";

const DASH = "—";

/**
 * Renders the current policy content as a table driven by `schema.columns`.
 *
 * Edit mode is opt-in per row: an add-row button lives above the table when the
 * user has edit permission; each row exposes edit + delete affordances.
 * Persistence is bubbled up as an updated `rows` array — the parent PUTs the
 * whole content back so the audit log always captures the full before/after.
 */
class PolicyViewer extends React.Component {
    state = {
        editorVisible: false,
        editorMode: "add", // 'add' | 'edit'
        editorRow: null,
        editorIndex: null,
    };

    openAdd = () =>
        this.setState({
            editorVisible: true,
            editorMode: "add",
            editorRow: null,
            editorIndex: null,
        });

    openEdit = (row, index) =>
        this.setState({
            editorVisible: true,
            editorMode: "edit",
            editorRow: row,
            editorIndex: index,
        });

    closeEditor = () =>
        this.setState({ editorVisible: false, editorRow: null, editorIndex: null });

    handleSubmit = row => {
        const { onRowsChange, policy } = this.props;
        const currentRows = (policy && policy.content && policy.content.rows) || [];
        const nextRows = currentRows.slice();
        if (this.state.editorMode === "add") {
            nextRows.push(row);
        } else if (this.state.editorIndex != null) {
            nextRows[this.state.editorIndex] = row;
        }
        this.closeEditor();
        if (onRowsChange) {
            onRowsChange(nextRows);
        }
    };

    handleDelete = index => {
        const { onRowsChange, policy } = this.props;
        const currentRows = (policy && policy.content && policy.content.rows) || [];
        const nextRows = currentRows.filter((_, i) => i !== index);
        if (onRowsChange) {
            onRowsChange(nextRows);
        }
    };

    renderCell(value) {
        if (value === null || value === undefined || value === "") {
            return DASH;
        }
        return String(value);
    }

    render() {
        const { policy, canEdit, saving } = this.props;
        if (!policy) {
            return <Empty description={"Select a policy from the left"} />;
        }
        const columns = (policy.schema && policy.schema.columns) || [];
        const rows = (policy.content && policy.content.rows) || [];
        return (
            <div>
                {canEdit ? (
                    <div style={{ marginBottom: 12, textAlign: "right" }}>
                        <Button
                            type={"primary"}
                            icon={"plus"}
                            onClick={this.openAdd}
                            disabled={saving}
                        >
                            Add row
                        </Button>
                    </div>
                ) : null}
                <div className={"policy-hub-table-wrap"}>
                    <table className={"policy-hub-table"}>
                        <thead>
                            <tr>
                                {columns.map(col => (
                                    <th key={col.key}>{col.label || col.key}</th>
                                ))}
                                {canEdit ? (
                                    <th className={"policy-hub-table__actions"}>
                                        Actions
                                    </th>
                                ) : null}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.length + (canEdit ? 1 : 0)
                                        }
                                        className={"policy-hub-table__empty"}
                                    >
                                        No rows yet.
                                    </td>
                                </tr>
                            ) : null}
                            {rows.map((row, i) => (
                                <tr key={i}>
                                    {columns.map(col => (
                                        <td key={col.key}>
                                            {this.renderCell(row[col.key])}
                                        </td>
                                    ))}
                                    {canEdit ? (
                                        <td
                                            className={"policy-hub-table__actions"}
                                        >
                                            <button
                                                type={"button"}
                                                className={
                                                    "policy-hub-table__row-btn"
                                                }
                                                onClick={() =>
                                                    this.openEdit(row, i)
                                                }
                                                disabled={saving}
                                                title={"Edit"}
                                            >
                                                <Icon type={"edit"} />
                                            </button>
                                            <Popconfirm
                                                title={"Delete this row?"}
                                                onConfirm={() =>
                                                    this.handleDelete(i)
                                                }
                                                okText={"Delete"}
                                                cancelText={"Cancel"}
                                            >
                                                <button
                                                    type={"button"}
                                                    className={
                                                        "policy-hub-table__row-btn policy-hub-table__row-btn--danger"
                                                    }
                                                    disabled={saving}
                                                    title={"Delete"}
                                                >
                                                    <Icon type={"delete"} />
                                                </button>
                                            </Popconfirm>
                                        </td>
                                    ) : null}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <PolicyRowEditor
                    visible={this.state.editorVisible}
                    mode={this.state.editorMode}
                    columns={columns}
                    initialRow={this.state.editorRow}
                    onSubmit={this.handleSubmit}
                    onCancel={this.closeEditor}
                />
            </div>
        );
    }
}

PolicyViewer.propTypes = {
    policy: PropTypes.object,
    canEdit: PropTypes.bool,
    saving: PropTypes.bool,
    onRowsChange: PropTypes.func,
};

export default PolicyViewer;
